import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
} from '@mui/icons-material';

interface CreateProjectModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (projectName: string) => void;
}

const CreateProjectModal: React.FC<CreateProjectModalProps> = ({
  open,
  onClose,
  onSubmit,
}) => {
  const [projectName, setProjectName] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = () => {
    if (!projectName.trim()) {
      setError('Project name is required');
      return;
    }

    if (projectName.trim().length < 3) {
      setError('Project name must be at least 3 characters');
      return;
    }

    onSubmit(projectName.trim());
    handleClose();
  };

  const handleClose = () => {
    setProjectName('');
    setError('');
    onClose();
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0px 24px 38px 3px rgba(0,0,0,0.14)',
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2.5,
          px: 3,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: '#F1F5FE',
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              fontSize: '1.5rem',
              color: 'text.primary',
            }}
          >
            Create New Product
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'text.secondary',
              fontSize: '1rem',
              fontWeight: 400,
              mt: 0.5,
            }}
          >
            Enter a name for your new product
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': {
              bgcolor: 'action.hover',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ mt: 1 }}>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mb: 2,
              fontSize: '0.875rem',
            }}
          >
            Choose a descriptive name that clearly identifies your product's purpose.
          </Typography>
          
          <TextField
            autoFocus
            fullWidth
            label="Product Name"
            variant="outlined"
            value={projectName}
            onChange={(e) => {
              setProjectName(e.target.value);
              if (error) setError('');
            }}
            onKeyPress={handleKeyPress}
            error={!!error}
            helperText={error || 'e.g., "Customer Portal Redesign", "Mobile App v2.0"'}
            placeholder="Enter product name..."
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
              },
            }}
            inputProps={{
              maxLength: 100,
            }}
          />
          
          {projectName.trim().length > 0 && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.disabled',
                fontSize: '0.75rem',
                mt: 1,
                display: 'block',
              }}
            >
              {projectName.trim().length}/100 characters
            </Typography>
          )}
        </Box>
      </DialogContent>

      {/* Actions */}
      <DialogActions
        sx={{
          p: 3,
          pt: 2,
          borderTop: 1,
          borderColor: 'divider',
          gap: 2,
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            borderColor: 'divider',
            color: 'text.secondary',
            minWidth: 100,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!projectName.trim() || projectName.trim().length < 3}
          sx={{
            bgcolor: 'primary.main',
            minWidth: 100,
            '&:hover': {
              bgcolor: 'primary.dark',
            },
            '&:disabled': {
              bgcolor: 'action.disabledBackground',
              color: 'action.disabled',
            },
          }}
        >
          Create Project
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateProjectModal;